"use client"

import { useMemo, useState, type ReactNode } from "react"
import { AlertTriangle, CheckCircle } from "lucide-react"
import {
  SectionRule,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { readNonNegative, readQuantity } from '@/lib/tools/numeric-input'
import { BreakdownMomentCTA } from "@/components/warranty/breakdown-moment-cta"
import type { ApprovedWarrantyCta } from "@/lib/warranty/model"

// Sources of truth (per docs/Page-Types-And-Templates.md):
//  • Rule 1 — age >= 10 ⇒ replace (ENERGY STAR 10-year energy cutoff).
//  • 50% rule — repair quote > 50% of replacement ⇒ replace
//    (Consumer Reports / USDA appliance guidance).
//  • DOE / AHAM avg refrigerator service life: 10–15 years.
//  • EIA US average residential rate: ~16.5¢/kWh (used as fallback when
//    no local rate is provided).

type Tone = "healthy" | "watch" | "replace"
type Recommendation = "repair" | "replace"

interface Verdict {
  recommendation: Recommendation
  tone: Tone
  headline: string
  subline: string
  reasons: string[]
  repair5yr: number
  replace5yr: number
  annualEnergySavings: number
  breakEvenYears: number | null
  confidence: "High" | "Medium"
}

const TONE_BORDER: Record<Tone, string> = {
  healthy: "#10a65c",
  watch: "#ff9951",
  replace: "#dc2626",
}
const TONE_BG: Record<Tone, string> = {
  healthy: "#ecfdf5",
  watch: "rgba(255,153,81,0.08)",
  replace: "#fef2f2",
}
const TONE_LABEL: Record<Tone, string> = {
  healthy: "Verdict · Repair",
  watch: "Verdict · Borderline",
  replace: "Verdict · Replace",
}
const TONE_CTA: Record<Tone, { label: string; href: string }> = {
  healthy: { label: "Find a service tech →", href: "/tools/refrigerator-repair-cost-estimator" },
  watch: { label: "Browse 2026 models →", href: "/collections/french-door-refrigerators" },
  replace: { label: "Shop replacements →", href: "/collections/french-door-refrigerators" },
}

function compute(
  age: number,
  repair: number,
  newCost: number,
  currentKwh: number,
  newKwh: number,
  rate: number,
): Verdict {
  // Preserved verbatim from legacy logic, with the ENERGY STAR 10-year hard
  // rule promoted to first-check.
  const annualEnergySavings = (currentKwh - newKwh) * rate
  const breakEvenYears =
    annualEnergySavings > 0 ? (newCost - repair) / annualEnergySavings : null
  const fiftyPercentRule = repair > newCost * 0.5
  const isPastEnergyStarCutoff = age >= 10
  const isOld = age > 8
  const significantEnergySavings = annualEnergySavings > 50
  const repair5yr = repair + currentKwh * rate * 5
  const replace5yr = newCost + newKwh * rate * 5

  const reasons: string[] = []
  let recommendation: Recommendation = "repair"
  let confidence: "High" | "Medium" = "Medium"

  // Hard rule first: 10-year ENERGY STAR cutoff
  if (isPastEnergyStarCutoff) {
    recommendation = "replace"
    confidence = "High"
    reasons.push(
      `Past the 10-year ENERGY STAR replace cutoff (age ${age} years). Newer ENERGY STAR models may use up to 35% less energy.`,
    )
  }

  if (fiftyPercentRule) {
    reasons.push(
      `Repair costs ${((repair / newCost) * 100).toLocaleString("en-US", { maximumFractionDigits: 0 })}% of new appliance — over the 50% rule (USDA appliance guidance).`,
    )
    recommendation = "replace"
    confidence = "High"
  }

  if (isOld) {
    reasons.push(`Appliance is ${age} years old — DOE / AHAM avg lifespan is 10–15 years.`)
    if (age > 12 && recommendation !== "replace") {
      recommendation = "replace"
      reasons.push("Past typical lifespan — more repairs likely.")
    }
  }

  if (significantEnergySavings) {
    reasons.push(
      `New model saves about $${annualEnergySavings.toLocaleString("en-US", { maximumFractionDigits: 0 })}/year in energy at your rate.`,
    )
    if (breakEvenYears !== null && breakEvenYears < 3 && recommendation !== "replace") {
      recommendation = "replace"
      reasons.push(`Energy savings pay back in ${breakEvenYears.toFixed(1)} years.`)
    }
  }

  if (replace5yr < repair5yr) {
    reasons.push(
      `5-year total cost: repair $${repair5yr.toLocaleString("en-US", { maximumFractionDigits: 0 })} vs replace $${replace5yr.toLocaleString("en-US", { maximumFractionDigits: 0 })}.`,
    )
    if (recommendation !== "replace") {
      recommendation = "replace"
      confidence = "High"
    }
  } else if (repair5yr < replace5yr * 0.8 && recommendation === "repair") {
    reasons.push("Repair is significantly cheaper over 5 years.")
    confidence = "High"
  }

  if (recommendation === "repair" && !isOld && !fiftyPercentRule) {
    confidence = "High"
  }

  let tone: Tone
  let headline: string
  let subline: string
  if (recommendation === "replace") {
    tone = "replace"
    headline = "Replace it."
    subline = isPastEnergyStarCutoff
      ? "Past the 10-year ENERGY STAR cutoff. Energy waste alone usually pays back a new unit in 2–4 years (per ENERGY STAR)."
      : "The repair cost, age, or 5-year math all point to replace. A new ENERGY STAR unit typically pays back fast on energy savings."
  } else if (confidence === "Medium") {
    tone = "watch"
    headline = "Borderline — repair, but plan ahead."
    subline =
      "Repair is the better number today, but the math is close. Start researching replacements so you're not caught flat-footed."
  } else {
    tone = "healthy"
    headline = "Repair it."
    subline =
      "The 5-year math clearly favors repair, you're under the 10-year cutoff, and the quote is well below half of replacement. Get it fixed."
  }

  return {
    recommendation,
    tone,
    headline,
    subline,
    reasons,
    repair5yr,
    replace5yr,
    annualEnergySavings,
    breakEvenYears,
    confidence,
  }
}

const RELATED = [
  {
    title: "Refrigerator Age Calculator",
    category: "Specialty",
    description: "Decode build date from your serial number.",
    href: "/tools/refrigerator-age-calculator",
    time: "~30 sec",
  },
  {
    title: "Refrigerator Cost of Ownership Calculator",
    category: "Energy & Cost",
    description: "True 10-year purchase + energy + maintenance cost.",
    href: "/tools/refrigerator-cost-of-ownership-calculator",
    time: "~45 sec",
  },
  {
    title: "Refrigerator Lifespan Calculator",
    category: "Specialty",
    description: "Estimate years of life remaining.",
    href: "/tools/refrigerator-lifespan-calculator",
    time: "~45 sec",
  },
  {
    title: "Refrigerator Repair Cost Estimator",
    category: "Specialty",
    description: "Typical repair pricing by component.",
    href: "/tools/refrigerator-repair-cost-estimator",
    time: "~30 sec",
  },
]

function InputLabel({ children, hint }: { children: ReactNode; hint?: string }) {
  return (
    <div className="mb-2">
      <label className="block text-[12px] font-bold uppercase tracking-[0.1em] text-gray-900">
        {children}
      </label>
      {hint && <span className="block text-[10px] text-gray-500 mt-0.5">{hint}</span>}
    </div>
  )
}

export function RepairVsReplaceCalculatorPageClient({
  approvedWarrantyCtas = [],
}: {
  /** FRIDGE-1042 — approved warranty programs from the registry; [] renders no slot. */
  approvedWarrantyCtas?: ApprovedWarrantyCta[]
} = {}) {
  const [age, setAge] = useState("8")
  const [repair, setRepair] = useState("350")
  const [newCost, setNewCost] = useState("1500")
  const [currentKwh, setCurrentKwh] = useState("700")
  const [newKwh, setNewKwh] = useState("450")
  const [rate, setRate] = useState("16.5")

  const verdict = useMemo(() => {
    const a = readNonNegative(age, { max: 50 })
    const rep = readQuantity(repair, { min: 1, max: 10000 })
    const nc = readQuantity(newCost, { min: 100, max: 30000 })
    const ck = readQuantity(currentKwh, { min: 50, max: 5000 })
    const nk = readQuantity(newKwh, { min: 50, max: 5000 })
    const r = readQuantity(rate, { min: 1, max: 100 })
    if (a === null || !rep || !nc || !ck || !nk || !r) return null
    return compute(a, rep, nc, ck, nk, r / 100)
  }, [age, repair, newCost, currentKwh, newKwh, rate])

  return (
    <ToolPageShell
      toolName="Refrigerator Repair vs Replace Calculator"
      heroEyebrow="Specialty"
      heroTitlePre="Should you fix it"
      heroTitleItalic="or buy a new one?"
      heroSubtitle="Six honest inputs. We apply the 50% rule (USDA appliance guidance), the 10-year ENERGY STAR cutoff, and a 5-year total-cost comparison — then give you a single verdict."
      heroMeta={["⏱ ~45 sec", "ENERGY STAR · DOE · AHAM · USDA"]}
      heroShare={<ToolShareBar title="Should you repair or replace your refrigerator?" />}
      faqs={[
        {
          question: "What is the 50% rule?",
          answer:
            "Widely cited in USDA appliance guidance: if a repair quote is more than half the cost of a new comparable unit, replacement is the better economic call. The calculator applies it for you.",
        },
        {
          question: "Why does age 10 tip the verdict to replace?",
          answer:
            "ENERGY STAR's energy-savings guidance: refrigerators 10 years or older may use up to 35% more energy than newer ENERGY STAR certified models. Replacement payback on energy alone is typically 2–4 years.",
        },
        {
          question: "What if my repair quote is low but the unit is 11 years old?",
          answer:
            "Hard rule wins. The 10-year ENERGY STAR cutoff overrides a cheap repair quote — the rest of the sealed system (compressor, evaporator, condenser, defrost heater) is aging in parallel and will need attention soon.",
        },
        {
          question: "Where do the lifespan numbers come from?",
          answer:
            "DOE appliance lifecycle data and AHAM industry surveys put refrigerator service life at 10–15 years depending on type. Top-freezer units last longest; compacts shortest.",
        },
        {
          question: "How honest is the 5-year math?",
          answer:
            "It's straightforward: repair cost + 5 years of current-unit energy at your rate, vs new cost + 5 years of new-unit energy at your rate. No financing, no resale, no maintenance baked in. Use the Cost of Ownership Calculator for the fully loaded view.",
        },
      ]}
      relatedTools={RELATED}
      cta={{
        title: "If it is time to replace, explore your options.",
        body: "Answer five questions to explore a starting product group, with prices where available. Verify dimensions, features and warranty terms on each product page.",
        buttonLabel: "Start fridge match",
        buttonHref: "/tools/find-your-fridge",
      }}
    >
      {/* ============ INPUTS + VERDICT ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid lg:grid-cols-[460px_1fr] gap-8 lg:gap-12">
          {/* Inputs */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-6 lg:sticky lg:top-24 lg:self-start">
            <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-5 text-[#ff9951]">
              Tell us about the repair
            </div>
            <div className="space-y-6">
              <div>
                <InputLabel hint="Years since manufacture">Age</InputLabel>
                <input
                  type="number"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>
              <div>
                <InputLabel hint="The quote you have in hand">Repair quote ($)</InputLabel>
                <input
                  type="number"
                  value={repair}
                  onChange={(e) => setRepair(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>
              <div>
                <InputLabel hint="A comparable new unit, fully delivered">New refrigerator cost ($)</InputLabel>
                <input
                  type="number"
                  value={newCost}
                  onChange={(e) => setNewCost(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>
              <div>
                <InputLabel hint="From the EnergyGuide label or estimate">Current kWh / year</InputLabel>
                <input
                  type="number"
                  value={currentKwh}
                  onChange={(e) => setCurrentKwh(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>
              <div>
                <InputLabel hint="ENERGY STAR full-size: ~400–500">New kWh / year</InputLabel>
                <input
                  type="number"
                  value={newKwh}
                  onChange={(e) => setNewKwh(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>
              <div>
                <InputLabel hint="US avg per EIA: ~16.5¢">Electricity rate (¢/kWh)</InputLabel>
                <input
                  type="number"
                  step="0.01"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  className="tool-input-clean w-full h-11 px-3 border border-gray-300 rounded-lg bg-white text-[14px] font-semibold"
                />
              </div>
            </div>
          </div>

          {/* Verdict */}
          <div>
            {verdict && (
              <>
                <div
                  className="rounded-2xl p-6 sm:p-8 md:p-10 mb-6 md:mb-8"
                  style={{
                    background: TONE_BG[verdict.tone],
                    border: `1px solid ${TONE_BORDER[verdict.tone]}30`,
                  }}
                >
                  <div
                    className="text-[11px] font-bold uppercase tracking-[0.18em] mb-6 flex items-center gap-2"
                    style={{ color: TONE_BORDER[verdict.tone] }}
                  >
                    <span
                      className="w-2 h-2 rounded-full inline-block"
                      style={{ background: TONE_BORDER[verdict.tone] }}
                    />
                    {TONE_LABEL[verdict.tone]} · {verdict.confidence} confidence
                  </div>
                  <div
                    className="text-gray-900 font-black"
                    style={{
                      fontSize: "clamp(40px, 6.5vw, 76px)",
                      letterSpacing: "-0.05em",
                      lineHeight: 0.95,
                      textWrap: "balance" as never,
                    }}
                  >
                    {verdict.headline}
                  </div>
                  <p
                    className="mt-5 md:mt-6 text-gray-700"
                    style={{ fontSize: 17, lineHeight: 1.45, textWrap: "pretty" as never }}
                  >
                    {verdict.subline}
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 border-y border-gray-300 sm:divide-x divide-gray-300 mb-6 md:mb-8">
                  <div className="py-5 sm:pr-5 px-0">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      5-yr repair
                    </div>
                    <div
                      className="font-black text-gray-900"
                      style={{ fontSize: 24, letterSpacing: "-0.03em" }}
                    >
                      {`$${verdict.repair5yr.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    </div>
                  </div>
                  <div className="py-5 sm:px-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      5-yr replace
                    </div>
                    <div
                      className="font-black"
                      style={{
                        fontSize: 24,
                        letterSpacing: "-0.03em",
                        color: TONE_BORDER[verdict.tone],
                      }}
                    >
                      {`$${verdict.replace5yr.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    </div>
                  </div>
                  <div className="py-5 sm:px-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Energy save / yr
                    </div>
                    <div
                      className="font-black text-gray-900"
                      style={{ fontSize: 24, letterSpacing: "-0.03em" }}
                    >
                      {`$${verdict.annualEnergySavings.toLocaleString("en-US", { maximumFractionDigits: 0 })}`}
                    </div>
                  </div>
                  <div className="py-5 sm:pl-5">
                    <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-gray-500 mb-1.5">
                      Break-even
                    </div>
                    <div
                      className="font-black text-gray-900"
                      style={{ fontSize: 24, letterSpacing: "-0.03em" }}
                    >
                      {verdict.breakEvenYears !== null && verdict.breakEvenYears > 0 && verdict.breakEvenYears < 99
                        ? `${verdict.breakEvenYears.toFixed(1)} yr`
                        : "—"}
                    </div>
                  </div>
                </div>

                {verdict.reasons.length > 0 && (
                  <div className="mb-8">
                    <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-gray-900">
                      Why this verdict
                    </div>
                    <ul className="space-y-2">
                      {verdict.reasons.map((r) => (
                        <li
                          key={r}
                          className="flex items-start gap-3 p-4 rounded-lg bg-white border border-gray-200"
                        >
                          <div
                            className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-white"
                            style={{ background: TONE_BORDER[verdict.tone] }}
                          >
                            {verdict.recommendation === "replace" ? (
                              <AlertTriangle className="w-4 h-4" />
                            ) : (
                              <CheckCircle className="w-4 h-4" />
                            )}
                          </div>
                          <p className="text-[14px] text-gray-700 leading-[1.55]">{r}</p>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <a
                  href={TONE_CTA[verdict.tone].href}
                  className="block h-14 rounded-lg text-[15px] font-extrabold tracking-tight text-white inline-flex items-center justify-center w-full"
                  style={{
                    background: verdict.tone === "replace" ? "#ff9951" : "#111827",
                    boxShadow:
                      verdict.tone === "replace" ? "0 8px 22px rgba(255,153,81,0.35)" : undefined,
                  }}
                >
                  {TONE_CTA[verdict.tone].label}
                </a>
              </>
            )}
          </div>
        </div>
      </section>

      {/* ============ BREAKDOWN-MOMENT SLOT (FRIDGE-1042) — nothing until approved ============ */}
      <BreakdownMomentCTA programs={approvedWarrantyCtas} context="a repair quote in hand" />

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="If it's time to replace"
          title="French door refrigerators worth the upgrade"
          description="Wider shelves, bottom-freezer access, ENERGY STAR–certified efficiency."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ METHODOLOGY ============ */}
      <section className="py-8 md:py-12 bg-gray-50 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <SectionRule eyebrow="How we decide" title="The methodology" />
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                n: "01",
                t: "10-year ENERGY STAR rule (hard)",
                d: "Refrigerators 10 years or older may use up to 35% more energy than newer ENERGY STAR certified models. Past the cutoff → replace, regardless of how cheap the repair is.",
              },
              {
                n: "02",
                t: "50% rule (USDA appliance guidance)",
                d: "If a repair quote is more than half the cost of a comparable new unit, replacement is the better economic call. The verdict flips the moment the math crosses 50%.",
              },
              {
                n: "03",
                t: "5-year total-cost comparison",
                d: "Repair cost + 5 yr of current-unit energy vs new cost + 5 yr of new-unit energy, both at your electricity rate. Whichever is lower wins — confidence is High when the gap is decisive.",
              },
            ].map((m) => (
              <div key={m.n}>
                <div className="font-mono text-[11px] font-bold tracking-[0.15em] mb-3 text-[#ff9951]">
                  {m.n}
                </div>
                <h3 className="text-[22px] font-extrabold tracking-[-0.02em] mb-3 text-gray-900">{m.t}</h3>
                <p className="text-[14px] text-gray-600 leading-[1.6]">{m.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SECONDARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Bottom-freezer refrigerators"
          description="Bend less, see more — the simplest layout to live with."
          collectionType="bottom-freezer"
          fallbackCollectionType="top-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
